"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, Sparkles, ArrowRight, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface PricingTier {
  id: string;
  name: string;
  subtitle: string;
  priceNote: string;
  isPopular: boolean;
  features: string[];
  ctaText: string;
}
const pricingTiers: PricingTier[] = [{
  id: "starter",
  name: "SEO starter package",
  subtitle: "For local businesses getting started",
  priceNote: "From £99/month, most local businesses invest £300–£500/month for effective results",
  isPopular: false,
  features: ["Initial website SEO audit", "Local keyword research", "Google Business Profile optimisation", "On-page SEO for core service pages", "Basic technical audits and fixes", "Local citation building", "Monthly performance report"],
  ctaText: "Book a free SEO consultation"
}, {
  id: "growth",
  name: "SEO growth package",
  subtitle: "For businesses scaling organic traffic",
  priceNote: "Typical investment: £1,000–£2,500 per month",
  isPopular: true,
  features: ["Everything in Starter, plus:", "Regular content creation and optimisation", "Social media marketing support", "Competitor analysis and strategy refinement", "Link building and authority development", "Advanced off-page optimisation", "Conversion tracking via Google Analytics & Search Console", "Bi-weekly or monthly strategy calls"],
  ctaText: "Book a free SEO consultation"
}, {
  id: "custom",
  name: "Custom SEO strategy",
  subtitle: "For comprehensive campaigns",
  priceNote: "Custom pricing based on scope, competition, and objectives",
  isPopular: false,
  features: ["Full technical SEO programme", "Comprehensive content strategy and creation", "Significant off-page SEO and link building", "Advanced strategies for competitive markets", "Detailed monthly reporting with revenue attribution", "Ongoing strategic consultation"],
  ctaText: "Book a free SEO consultation"
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export default function Pricing() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="pricingpackages" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 font-sans text-xs tracking-wide">
            Transparent investment
          </Badge>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-6">
            Affordable SEO packages and pricing
          </h2>
          <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
            I believe in transparent pricing that matches your business stage, site size, and goals. My affordable packages start from £99 per month, with support for businesses that need ongoing help without agency-level retainers. What ultimately affects SEO costs is a combination of website size, your industry&apos;s competition level, the underlying technical condition of your site, your target market (whether you need focused local exposure or broader{" "}
            <Link to="/NationalSeoPage" className="text-primary underline underline-offset-2 hover:text-primary/80">
              national
            </Link>{" "}
            visibility), content requirements, technical implementation requirements, and the level of ongoing support needed. Businesses wanting ongoing month-to-month support can read more about that on the{" "}
            <Link to="/MonthlySeoServicesPage" className="text-primary underline underline-offset-2 hover:text-primary/80">
              Monthly SEO Services
            </Link>{" "}
            page. SEO costs for small businesses typically range from £300 to £1,200 monthly for meaningful, results-driven work.
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <motion.div variants={shouldReduceMotion ? {} : containerVariants} initial={shouldReduceMotion ? "show" : "hidden"} whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch mb-16">
          {pricingTiers.map((tier, i) => <motion.div key={tier.id} data-index={i} variants={shouldReduceMotion ? {} : cardVariants} className="flex">
              <Card className={`flex-1 flex flex-col justify-between relative rounded-2xl border transition-[border-color,box-shadow] duration-300 ${tier.isPopular ? "border-primary shadow-xl bg-card ring-1 ring-primary/20 lg:-translate-y-2" : "border-border shadow-md bg-card/60 hover:border-primary/40 hover:shadow-lg"}`}>
                {tier.isPopular && <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-primary text-primary-foreground shadow-sm">
                      <Sparkles className="size-3.5" />
                      Most popular choice
                    </span>
                  </div>}

                <div className="p-6 sm:p-8 flex-1 flex flex-col">
                  <CardHeader className="p-0 mb-6">
                    <CardTitle className="font-serif text-2xl font-bold text-foreground">
                      {tier.name}
                    </CardTitle>
                    <CardDescription className="font-sans text-sm text-muted-foreground mt-1">
                      {tier.subtitle}
                    </CardDescription>
                  </CardHeader>

                  <div className="p-4 rounded-xl bg-muted/60 border border-border/70 mb-6">
                    <p className="font-sans text-xs sm:text-sm font-medium text-foreground leading-snug">
                      {tier.priceNote}
                    </p>
                  </div>

                  <CardContent className="p-0 flex-1">
                    <p className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                      What is included:
                    </p>
                    <ul className="space-y-3">
                      {tier.features.map((feature, featureIndex) => {
                    const isHeader = feature.endsWith(":");
                    return <li key={featureIndex} data-index={featureIndex} className={`flex items-start gap-3 font-sans text-sm ${isHeader ? "font-semibold text-foreground pt-1 pb-0.5" : "text-muted-foreground"}`}>
                            {!isHeader && <div className="size-5 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                                <Check className="size-3.5 stroke-[2.5]" />
                              </div>}
                            <span className={isHeader ? "text-foreground" : ""}>{feature}</span>
                          </li>;
                  })}
                    </ul>
                  </CardContent>
                </div>

                <CardFooter className="p-6 sm:p-8 pt-0">
                  <Button asChild variant={tier.isPopular ? "default" : "outline"} className={`w-full font-sans font-medium h-11 transition-[background-color,border-color,color] ${tier.isPopular ? "bg-primary text-primary-foreground hover:bg-primary/90" : "hover:bg-primary hover:text-primary-foreground hover:border-primary"}`}>
                    <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                      <span>{tier.ctaText}</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Closing Consultation Note */}
        <div className="max-w-3xl mx-auto rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm text-center">
          <div className="inline-flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary mb-4">
            <HelpCircle className="size-5" />
          </div>
          <h3 className="font-serif text-lg sm:text-xl font-semibold text-foreground mb-2">
            Not sure which package is right?
          </h3>
          <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
            Book a free SEO consultation and I&apos;ll recommend the right approach based on your business, budget, and goals. My approach is to recommend only what you actually need. Your budget should guide expected outcomes – I&apos;d rather have an honest conversation about what&apos;s realistic than sell you something that won&apos;t deliver.
          </p>
          <Button asChild variant="default" className="font-sans">
            <Link to="/ContactPage">
              Book a free SEO consultation
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}